import { useCallback, useMemo } from 'react';

import type { GlobalDialogData, GlobalDialogType, OpenGlobalDialogOptions } from '../global-dialogs.store.types';

import { getDialogKeyByPageContext } from 'shared/legacy/contexts/DialogContext/getDialogKeyByPageContext';
import { getPageContext } from 'utils/getPageContext';
import { useGlobalDialogsStore } from '../global-dialogs.store';

/**
 * Hook for managing global dialogs with type safety and automatic key generation.
 *
 * This hook provides two usage patterns:
 * 1. **Typed version**: Pass a specific dialog type to get a controller for that dialog
 * 2. **Untyped version**: Get a general controller where you specify the dialog type when calling methods
 *
 * @template T - The specific GlobalDialogType when using the typed version
 *
 * @param type - Optional dialog type for typed usage. When provided, the returned methods
 *               will be bound to this specific dialog type.
 *
 * @returns An object with `open` and `close` methods for managing the dialog
 *
 * @example
 * **Typed usage** - Recommended when working with a specific dialog repeatedly:
 * ```tsx
 * const shouldConnectEmailDialog = useGlobalDialog(GlobalDialogType.ShouldConnectEmail);
 *
 * // Opens the dialog (no payload needed for this type)
 * shouldConnectEmailDialog.open();
 *
 * // Closes the dialog
 * shouldConnectEmailDialog.close();
 * ```
 *
 * @example
 * **Typed usage with payload** - For dialogs that require data:
 * ```tsx
 * const candidateOutreachDialog = useGlobalDialog(GlobalDialogType.CandidateOutreach);
 *
 * // Opens the dialog with required payload
 * candidateOutreachDialog.open({
 *   payload: {
 *     candidate: selectedCandidate,
 *     outreachType: OutreachType.Email
 *   }
 * });
 *
 * candidateOutreachDialog.close();
 * ```
 *
 * @example
 * **Untyped usage** - Useful when working with multiple dialog types:
 * ```tsx
 * const globalDialog = useGlobalDialog();
 *
 * // Open different dialog types
 * globalDialog.open(GlobalDialogType.ShouldConnectEmail);
 * globalDialog.open(GlobalDialogType.CandidateOutreach, {
 *   payload: {
 *     candidate: selectedCandidate,
 *     outreachType: OutreachType.Email
 *   }
 * });
 *
 * // Close specific dialogs
 * globalDialog.close(GlobalDialogType.ShouldConnectEmail);
 * globalDialog.close(GlobalDialogType.CandidateOutreach);
 * ```
 *
 * @example
 * **Using custom keys** - For dialogs that need to be differentiated by context:
 * ```tsx
 * const campaignDialog = useGlobalDialog(GlobalDialogType.CampaignPhoneReachOut);
 *
 * // Open dialog with a specific key (e.g., campaign ID)
 * campaignDialog.open({
 *   key: 'campaign-123',
 *   payload: {
 *     // campaign-specific data
 *   }
 * });
 * ```
 *
 * @remarks
 * **Key Management**:
 * - If no key is provided, a contextual key is generated based on the current page context
 * - Keys are used to identify dialog instances when multiple instances of the same dialog type can exist
 * - For example, `CampaignPhoneReachOut` dialogs use campaign/search IDs as keys
 * - Page-specific dialogs (inbox, shared, candidate pages) use page context as keys
 *
 * **Payload Requirements**:
 * - TypeScript enforces payload requirements based on dialog type
 * - If `GlobalDialogData<T>` extends `never`, no payload is required or allowed
 * - If `GlobalDialogData<T>` has a defined type, payload is required in the `options` parameter
 *
 * **Storage**:
 * - Dialog data is stored in the global dialogs store
 * - Multiple dialog instances can be stored, but only one can be active at a time
 * - Dialog state persists across page navigation when using appropriate keys
 */
export function useGlobalDialog<T extends GlobalDialogType>(
  type: T,
): {
  open: (
    ...args: GlobalDialogData<T> extends never
      ? [options?: Omit<OpenGlobalDialogOptions<T>, 'payload'>]
      : [options: OpenGlobalDialogOptions<T>]
  ) => void;
  close: () => void;
};

/**
 * Hook for managing global dialogs with type safety and automatic key generation.
 *
 * This overload provides an untyped version where you specify the dialog type when calling methods.
 *
 * @returns An object with `open` and `close` methods that accept dialog type as first parameter
 *
 * @see {@link useGlobalDialog} for detailed documentation and examples
 */
export function useGlobalDialog(): {
  open: <T extends GlobalDialogType>(
    type: T,
    ...args: GlobalDialogData<T> extends never
      ? [options?: Omit<OpenGlobalDialogOptions<T>, 'payload'>]
      : [options: OpenGlobalDialogOptions<T>]
  ) => void;
  close: (dialogType: GlobalDialogType) => void;
};

export function useGlobalDialog<T extends GlobalDialogType>(type?: T) {
  const addActiveDialog = useGlobalDialogsStore((s) => s.addActiveDialog);
  const removeActiveDialog = useGlobalDialogsStore((s) => s.removeActiveDialog);
  const setGlobalDialogData = useGlobalDialogsStore((s) => s.set);

  const openWithType = useCallback(
    <T extends GlobalDialogType>(
      type: T,
      ...args: GlobalDialogData<T> extends never
        ? [options?: Omit<OpenGlobalDialogOptions<T>, 'payload'>]
        : [options: OpenGlobalDialogOptions<T>]
    ) => {
      const [options] = args;

      if (options && 'payload' in options) {
        const contextualKey = getDialogKeyByPageContext(type, getPageContext(), options.key ?? 'fallback-key');

        setGlobalDialogData(type, options.payload, contextualKey);
        addActiveDialog({ type, key: contextualKey });
      } else {
        const contextualKey = getDialogKeyByPageContext(type, getPageContext(), options?.key ?? 'fallback-key');

        addActiveDialog({ type, key: contextualKey });
      }
    },
    [setGlobalDialogData, addActiveDialog],
  );

  const openWithoutType = useCallback(
    (
      ...args: GlobalDialogData<T> extends never
        ? [options?: Omit<OpenGlobalDialogOptions<T>, 'payload'>]
        : [options: OpenGlobalDialogOptions<T>]
    ) => {
      if (!type) {
        throw new Error('Type is required when using openWithoutType');
      }

      openWithType(type, ...args);
    },
    [openWithType, type],
  );

  const closeWithType = useCallback(
    (dialogType: GlobalDialogType) => {
      removeActiveDialog(dialogType);
    },
    [removeActiveDialog],
  );

  const closeWithoutType = useCallback(() => {
    if (!type) {
      throw new Error('Type is required when using closeWithoutType');
    }

    closeWithType(type);
  }, [closeWithType, type]);

  return useMemo(
    () => ({
      open: type ? openWithoutType : openWithType,
      close: type ? closeWithoutType : closeWithType,
    }),
    [openWithoutType, openWithType, closeWithType, type],
  );
}
